import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getInvoice, updateInvoice, deleteInvoice, getInvoicePdfUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, 
  Edit2, 
  Trash2,
  Send,
  Check,
  Printer,
  Download,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { Client360Link } from '../../components/ui/client-360-link';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const statusLabels = {
  draft: 'Borrador',
  sent: 'Enviada',
  paid: 'Pagada',
  overdue: 'Vencida',
  cancelled: 'Cancelada'
};

const StatusBadge = ({ status }) => {
  return (
    <span className={`status-badge status-${status} text-sm`}>
      {statusLabels[status] || status}
    </span>
  );
};

export const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      const { data } = await getInvoice(id);
      setInvoice(data);
    } catch (e) {
      toast.error('Error al cargar factura');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateInvoice(id, { status: newStatus });
      toast.success('Estado actualizado');
      loadInvoice();
    } catch (e) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
    try {
      await deleteInvoice(id);
      toast.success('Factura eliminada');
      navigate('/invoices');
    } catch (e) {
      toast.error('Error al eliminar factura');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    window.open(getInvoicePdfUrl(id), '_blank');
    toast.success('Descargando PDF...');
  };

  const handleSendEmail = () => {
    const clientEmail = invoice.client?.email || '';
    const subject = encodeURIComponent(`Factura ${invoice.invoice_number}`);
    const body = encodeURIComponent(
`Estimado/a ${invoice.client?.name || 'cliente'},

Adjunto le envío la factura ${invoice.invoice_number} por un importe de ${formatCurrency(invoice.total)}.

Fecha de emisión: ${invoice.issue_date}
Fecha de vencimiento: ${invoice.due_date}

Puede descargar la factura en PDF desde el enlace que le facilito.

Gracias por su confianza.

Saludos cordiales`
    );
    
    window.open(`mailto:${clientEmail}?subject=${subject}&body=${body}`, '_blank');
    toast.success('Abriendo cliente de correo...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between print:hidden gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 hover:bg-stone-100 rounded-xl transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight font-mono">
                {invoice.invoice_number}
              </h1>
              <StatusBadge status={invoice.status} />
            </div>
            <p className="text-stone-500 text-sm mt-1">
              Emitida el {invoice.issue_date}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            className="rounded-xl"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>

          <Button
            variant="outline"
            onClick={handleSendEmail}
            className="rounded-xl"
            data-testid="send-email-button"
          >
            <Mail className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Email</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handlePrint}
            className="rounded-xl hidden sm:inline-flex"
            data-testid="print-button"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          
          {invoice.status === 'draft' && (
            <Button
              onClick={() => handleStatusChange('sent')}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
              data-testid="mark-sent-button"
            >
              <Send className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Marcar Enviada</span>
            </Button>
          )}
          
          {invoice.status === 'sent' && (
            <Button
              onClick={() => handleStatusChange('paid')}
              className="bg-green-600 hover:bg-green-700 rounded-xl"
              data-testid="mark-paid-button"
            >
              <Check className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Marcar Pagada</span>
            </Button>
          )}

          <Link to={`/invoices/${id}/edit`}>
            <Button variant="outline" className="rounded-xl" data-testid="edit-invoice-button">
              <Edit2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Editar</span>
            </Button>
          </Link>

          <Button
            variant="outline"
            onClick={handleDelete}
            className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="delete-invoice-button"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Invoice Document */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-8 print:border-0 print:p-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-stone-100 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-heading font-black tracking-tight text-[#FF6B4A]">FACTURA</h2>
            <p className="text-xl sm:text-2xl font-mono font-bold mt-1">{invoice.invoice_number}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-sm text-stone-500">Fecha de emisión</p>
            <p className="font-medium">{invoice.issue_date}</p>
            <p className="text-sm text-stone-500 mt-2">Fecha de vencimiento</p>
            <p className="font-medium">{invoice.due_date}</p>
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">FACTURAR A</p>
            <Client360Link clientId={invoice.client?.id || invoice.client_id} testid="invoice-detail-view-360" />
          </div>
          {invoice.client && (
            <div>
              <p className="font-bold text-lg">{invoice.client.name}</p>
              {invoice.client.tax_id && (
                <p className="text-sm text-stone-600 font-mono">{invoice.client.tax_id}</p>
              )}
              {invoice.client.address && (
                <p className="text-sm text-stone-600">{invoice.client.address}</p>
              )}
              {(invoice.client.postal_code || invoice.client.city) && (
                <p className="text-sm text-stone-600">
                  {[invoice.client.postal_code, invoice.client.city].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Items Table */}
        <div className="mb-8 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-stone-900">
                <th className="text-left py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Descripción</th>
                <th className="text-right py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 w-24">Cantidad</th>
                <th className="text-right py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 w-32">Precio</th>
                <th className="text-right py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 w-20">IVA</th>
                <th className="text-right py-3 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 w-32">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, index) => {
                const lineTotal = item.quantity * item.unit_price;
                return (
                  <tr key={index} className="border-b border-stone-100">
                    <td className="py-4">{item.description}</td>
                    <td className="py-4 text-right font-mono">{item.quantity}</td>
                    <td className="py-4 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                    <td className="py-4 text-right font-mono">{item.tax_rate}%</td>
                    <td className="py-4 text-right font-mono font-medium">{formatCurrency(lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-72">
            <div className="flex justify-between py-2 border-b border-stone-100">
              <span className="text-stone-500">Subtotal</span>
              <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-stone-100">
              <span className="text-stone-500">IVA (21%)</span>
              <span className="font-mono">{formatCurrency(invoice.tax_total)}</span>
            </div>
            <div className="flex justify-between py-3 text-xl font-bold">
              <span>Total</span>
              <span className="font-mono text-[#FF6B4A]">{formatCurrency(invoice.total)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="mt-8 pt-8 border-t border-stone-100">
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-2">NOTAS</p>
            <p className="text-sm text-stone-600">{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
